import { getOssSecurity } from '@/services/oss'
import { VoiceFactoryUploadLifecycle } from '../utils/upload/voiceFactoryLifecycle.mjs';

class UploadManager {
    constructor() {
        if (!UploadManager.instance) {
            this.pendingMessages = {};
            this.isWorkerReady = {};
            this.callbacks = {};
            this.voiceFactoryUploads = new VoiceFactoryUploadLifecycle(
                () => this.emit('VOICE_FACTORY_UPLOAD_STATE', this.hasActiveVoiceFactoryUploads()),
                payload => this.emit('VOICE_FACTORY_UPLOAD_ERROR', payload)
            );
            this.ossPath = {};
            this.initPromises = {}; // 新增：存储每个 ossId 的初始化 Promise
            this.restartWorker();
            UploadManager.instance = this;
        }
        return UploadManager.instance;
    }

    restartWorker() {
        if (this.worker) {
            this.worker.terminate();
        }
        this.worker = new Worker('/upload.worker.js');
        this.isWorkerReady = {};
        this.setupListeners();
    }

    setupListeners() {
        this.worker.onmessage = (e) => {
            const { type, payload } = e.data;

            if (type === 'UPLOAD_TASK_SETTLED') {
                this.finishVoiceFactoryUpload(payload.datasetId);
                return;
            }

            if (type === 'UPLOAD_ERROR' && this.voiceFactoryUploads.tasks.has(payload.datasetId)) {
                this.emit('VOICE_FACTORY_UPLOAD_ERROR', payload);
            }

            // Worker就绪通知
            if (type === 'WORKER_READY') {
                this.isWorkerReady[payload.ossId] = true;

                // 如果有对应的初始化 Promise，解析它
                if (this.initPromises[payload.ossId]) {
                    this.initPromises[payload.ossId].resolve();
                    delete this.initPromises[payload.ossId];
                }

                if (this.pendingMessages[payload.ossId]) {
                    this.pendingMessages[payload.ossId].forEach(msg => this.worker.postMessage(msg));
                    this.pendingMessages[payload.ossId] = [];
                }
                return;
            }

            // 重试通知
            if (type === "RETRY_NOTICE") {
                console.warn(
                    `[重试 ${payload.retryCount}/3] ${payload.filePath}: ${payload.error}`
                )
                return
            }

            // 上传失败通知
            if (type === "UPLOAD_FAILED") {
                if (payload.isFinalFailure) {
                    console.warn(`上传最终失败: ${payload.error || ""}`);
                }
                return
            }

            // 原有回调处理
            if (this.callbacks[type]) {
                this.callbacks[type].forEach(cb => {
                    if (['CHUNK_COMPLETE', 'CHUNK_ADD_COMPLETE'].includes(type)
                        && this.voiceFactoryUploads.tasks.has(payload.packetId)) {
                        this.voiceFactoryUploads.enqueueProgress(payload.packetId, () => cb(payload));
                    } else {
                        cb(payload);
                    }
                });
            }
        };

        this.worker.onerror = (e) => {
            console.error('Worker error:', e);
            this.voiceFactoryUploads.tasks.forEach((_, datasetId) => {
                this.emit('VOICE_FACTORY_UPLOAD_ERROR', {
                    datasetId,
                    error: '上传进程异常，请保留原文件并在数据集详情中续传'
                });
                this.finishVoiceFactoryUpload(datasetId);
            });
            setTimeout(() => this.restartWorker(), 1000);
        };
    }

    getListenerCount(event) {
        if (!this.callbacks[event]) return 0;
        return this.callbacks[event].length;
    }

    on(event, callback) {
        if (!this.callbacks[event]) this.callbacks[event] = [];
        this.callbacks[event].push(callback);
        return () => {
            this.callbacks[event] = this.callbacks[event].filter(cb => cb !== callback);
        };
    }

    emit(event, payload) {
        (this.callbacks[event] || []).forEach(callback => callback(payload));
    }

    hasActiveVoiceFactoryUploads() {
        return this.voiceFactoryUploads.isActive();
    }

    beginVoiceFactoryPreparation() {
        return this.voiceFactoryUploads.beginPreparation();
    }

    isVoiceFactoryUpload(datasetId) {
        return this.voiceFactoryUploads.tasks.has(datasetId);
    }

    async finishVoiceFactoryUpload(datasetId) {
        const result = await this.voiceFactoryUploads.settle(datasetId);
        if (!result) return;
        this.emit('VOICE_FACTORY_UPLOAD_SETTLED', { datasetId });
    }

    async init(ossId, ossDir) {
        // 如果同一个 ossId 已经在初始化中，返回同一个 Promise
        if (this.initPromises[ossId]) {
            return this.initPromises[ossId].promise;
        }

        // 创建 Promise 并存储解析函数
        let resolve, reject;
        const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
        });

        this.initPromises[ossId] = { promise, resolve, reject };

        try {
            const res = await getOssSecurity(ossId);
            if (res.code === 0) {
                this.worker.postMessage({
                    type: 'INIT_CLIENT',
                    payload: {
                        ossId,
                        data: res.data,
                        token: localStorage.getItem("dots-token") || sessionStorage.getItem("dots-token") || ""
                    }
                });
                this.ossPath[ossId] = `https://${res.data.bucket}.${res.data.region}.aliyuncs.com/${ossDir}/`;

                // 返回 Promise，将在 WORKER_READY 时被解析
                return promise;
            } else {
                // 如果获取安全凭证失败，拒绝 Promise
                this.initPromises[ossId].reject(new Error('Failed to get OSS security'));
                delete this.initPromises[ossId];
                throw new Error('Failed to get OSS security');
            }
        } catch (error) {
            // 如果发生错误，拒绝 Promise
            if (this.initPromises[ossId]) {
                delete this.initPromises[ossId];
            }
            throw error;
        }
    }

    upload(task, ossId, ossDir) {
        const self = this
        return new Promise((resolve, reject) => {
            this.voiceFactoryUploads.start(task);
            const message = {
                type: 'ENQUEUE_TASK',
                payload: { task, ossId, ossDir }
            };
            try {
                if (self.isWorkerReady[ossId]) {
                    self.worker.postMessage(message);
                } else {
                    if (!self.pendingMessages[ossId]) {
                        self.pendingMessages[ossId] = []
                    }
                    self.pendingMessages[ossId].push(message);
                }
            } catch (error) {
                if (task.scene === 3) {
                    self.emit('VOICE_FACTORY_UPLOAD_ERROR', { datasetId: task.datasetId, error: error.message });
                    self.finishVoiceFactoryUpload(task.datasetId);
                }
                reject(error);
            }
        });
    }

    pause() {
        this.worker.postMessage({ type: 'PAUSE' });
    }

    resume() {
        this.worker.postMessage({ type: 'RESUME' });
    }

    terminate() {
        this.worker.terminate();
        UploadManager.instance = null;
    }
}

const uploadManager = new UploadManager();

export default uploadManager;
